import axios, { AxiosError } from 'axios'
import type { AxiosInstance, AxiosRequestConfig } from 'axios'
import env from '../configs/env'

const TOKEN_KEY = 'cspg.token'

export const tokenStore = {
    get: () => localStorage.getItem(TOKEN_KEY),
    set: (token: string) => localStorage.setItem(TOKEN_KEY, token),
    clear: () => localStorage.removeItem(TOKEN_KEY)
}

const api: AxiosInstance = axios.create({
    baseURL: env.API_BASE_URL,
    headers: { 'Content-Type': 'application/json' }
})

api.interceptors.request.use((cfg) => {
    const token = tokenStore.get()
    if (token && cfg.headers) {
        cfg.headers['Authorization'] = `Bearer ${token}`
    }

    if (typeof FormData !== 'undefined' && cfg.data instanceof FormData && cfg.headers) {
        delete cfg.headers['Content-Type']
        delete cfg.headers['content-type']
    }

    return cfg
})

api.interceptors.response.use(
    (r) => r,
    (error: AxiosError<{ success?: boolean; message?: string }>) => {
        if (error.response?.status === 401) {
            tokenStore.clear()
            if (!window.location.pathname.startsWith('/dashboard/login')) {
                const redirect = encodeURIComponent(window.location.pathname + window.location.search)
                window.location.assign(`/dashboard/login?redirect=${redirect}`)
            }
        }
        return Promise.reject(error)
    }
)

export class ApiError extends Error {
    status?: number
    payload?: unknown
    constructor(message: string, status?: number, payload?: unknown) {
        super(message)
        this.name = 'ApiError'
        this.status = status
        this.payload = payload
    }
}

function extractMessage(err: unknown, fallback: string): string {
    const axErr = err as AxiosError<{ message?: string }>
    return axErr.response?.data?.message || axErr.message || fallback
}

export async function apiGet<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
        const r = await api.get<T>(url, config)
        return r.data
    } catch (err) {
        const ax = err as AxiosError
        throw new ApiError(extractMessage(err, 'Request failed'), ax.response?.status, ax.response?.data)
    }
}

export async function apiPost<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
    try {
        const r = await api.post<T>(url, body, config)
        return r.data
    } catch (err) {
        const ax = err as AxiosError
        throw new ApiError(extractMessage(err, 'Request failed'), ax.response?.status, ax.response?.data)
    }
}

export async function apiPut<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
    try {
        const r = await api.put<T>(url, body, config)
        return r.data
    } catch (err) {
        const ax = err as AxiosError
        throw new ApiError(extractMessage(err, 'Request failed'), ax.response?.status, ax.response?.data)
    }
}

export async function apiDelete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
        const r = await api.delete<T>(url, config)
        return r.data
    } catch (err) {
        const ax = err as AxiosError
        throw new ApiError(extractMessage(err, 'Request failed'), ax.response?.status, ax.response?.data)
    }
}

export async function apiPostForm<T>(url: string, fd: FormData): Promise<T> {
    return apiPost<T>(url, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
}

export async function apiPutForm<T>(url: string, fd: FormData): Promise<T> {
    return apiPut<T>(url, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
}

export function apiUrl(path: string): string {
    const base = env.API_BASE_URL.replace(/\/$/, '')
    const tail = path.startsWith('/') ? path : `/${path}`
    return `${base}${tail}`
}

export default api
